import { Nullable } from '../../types/common'
import { hideElement, showElement } from '../../utils/dom'
import BaseComponent from '../base-component/base-component'
import ProductCard from './product-card'
import { capitalize } from '../../utils/utils'

class ProductCardColorSwatches extends BaseComponent {
  hiddenCount: Nullable<HTMLElement>
  label: Nullable<HTMLElement>
  items: HTMLElement[]
  productCard: Nullable<ProductCard>
  mainImage: Nullable<HTMLElement>
  currentVisibleImage: Nullable<HTMLElement>

  constructor() {
    super()

    this.hiddenCount = this.querySelector('[data-color-swatches-hidden-count]')
    this.label = this.querySelector('[data-color-swatches-label]')
    this.items = [
      ...this.querySelectorAll('[data-color-swatches-item]'),
    ] as HTMLElement[]
    this.productCard = this.closest<ProductCard>('product-card')

    if (this.productCard) {
      this.mainImage = this.productCard.querySelector('[data-main-media]')
    }
  }

  connectedCallback(): void {
    this.initOffsets()
    this.update()

    this.items.forEach(item => {
      item.addEventListener('mouseenter', this.handleSwatchEnter)
      item.addEventListener('mouseleave', this.handleSwatchLeave)
    })
  }

  disconnectedCallback(): void {
    this.items.forEach(item => {
      item.removeEventListener('mouseenter', this.handleSwatchEnter)
      item.removeEventListener('mouseleave', this.handleSwatchLeave)
    })
  }

  handleSwatchEnter = (event: MouseEvent): void => {
    if (!this.productCard) {
      return
    }

    const target = event.target as HTMLElement
    const variantId = target.dataset.variantId
    const image = this.productCard.querySelector<HTMLElement>(
      `[data-variant-id="${variantId}"]`,
    )
    const color = target.dataset.color

    if (!image || !this.mainImage || !color) {
      return
    }

    this.updateLabel(color)

    this.setImageVisible(image, true)
    this.setImageVisible(this.mainImage, false)

    this.currentVisibleImage = image

    this.setHoverStatus(true)
  }

  handleSwatchLeave = (): void => {
    this.updateLabel('')

    if (this.currentVisibleImage) {
      this.setImageVisible(this.currentVisibleImage, false)
    }

    if (this.mainImage) {
      this.setImageVisible(this.mainImage, true)
    }

    this.setHoverStatus(false)
  }

  update(): void {
    this.updateSwatchesVisible()
    this.updateCount()
    this.updateDetailsInner()
  }

  updateDetailsInner() {
    const detailsInner = this.closest('[data-product-details-inner]')

    if (!detailsInner) {
      return
    }

    if (detailsInner.offsetHeight < this.offsetHeight) {
      detailsInner.style.minHeight = `${this.offsetHeight}px`
    }
  }

  setImageVisible(img: HTMLElement, isVisible: boolean): void {
    img.classList.toggle('product-card__img--visible', isVisible)
  }

  updateSwatchesVisible(): void {
    if (!this.productCard) {
      return
    }

    this.items.forEach(swatch => {
      const swatchRightPosition = swatch.offsetLeft + swatch.offsetWidth

      const maxWidth = this.parentNode.offsetWidth - swatch.offsetWidth
      const isOverflowed = swatchRightPosition > maxWidth

      swatch.classList.toggle('hidden-swatch', isOverflowed)
    })
  }

  initOffsets() {
    this.items.forEach(swatch => {
      swatch.style.left = `${swatch.offsetLeft}px`
    })
  }

  updateCount(): void {
    if (!this.hiddenCount) {
      return
    }

    const notHiddenSwatches = this.items.filter(
      item => !item.classList.contains('hidden-swatch'),
    )

    const count = +(this.dataset.count || 0) - notHiddenSwatches.length
    const hiddenCountLabel = this.querySelector(
      '[data-color-swatches-hidden-count-label]',
    )

    if (!hiddenCountLabel) {
      return
    }

    if (count > 0) {
      showElement(this.hiddenCount)

      hiddenCountLabel.innerText = `+${count}`
    } else {
      hideElement(this.hiddenCount)

      hiddenCountLabel.innerText = ''
    }
  }

  updateLabel(color: string): void {
    if (!this.label) {
      return
    }

    this.label.innerText = color ? capitalize(color) : ''
  }

  setHoverStatus(isHover: boolean) {
    this.productCard?.classList.toggle('is-color-swatch-hover', isHover)
  }
}

export default ProductCardColorSwatches
